import { differenceInCalendarDays } from '@cobliteam/utils/date';
import { useEffect, useState } from 'react';

const STORAGE_KEY_PREFIX = 'completion_beacon:goalReachedAt:';

const getStorageKey = (fleetId: string) => `${STORAGE_KEY_PREFIX}${fleetId}`;

const safeGet = (key: string): string | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string) => {
  try {
    if (typeof window !== 'undefined') window.localStorage.setItem(key, value);
  } catch {
    // no-op
  }
};

type UseGoalReachedAtInput = {
  installedPct: number;
  goalPct: number;
};

type UseGoalReachedAtResult = {
  goalReachedAt: Date | null;
  isWithinGoalWindowDays: (days: number) => boolean;
};

export const useGoalReachedAt = (fleetId: string | null | undefined, { installedPct, goalPct }: UseGoalReachedAtInput): UseGoalReachedAtResult => {
  const [goalReachedAt, setGoalReachedAt] = useState<Date | null>(() => {
    if (!fleetId) return null;
    const raw = safeGet(getStorageKey(fleetId));
    if (!raw) return null;
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  });

  useEffect(() => {
    if (!fleetId) return;
    if (goalReachedAt) return;
    if (installedPct < goalPct) return;

    const now = new Date();
    safeSet(getStorageKey(fleetId), now.toISOString());
    setGoalReachedAt(now);
  }, [fleetId, goalReachedAt, installedPct, goalPct]);

  const isWithinGoalWindowDays = (days: number): boolean => {
    if (!goalReachedAt) return false;
    const diff = differenceInCalendarDays(new Date(), goalReachedAt);
    return diff <= days;
  };

  return { goalReachedAt, isWithinGoalWindowDays };
};

export default useGoalReachedAt;
